import { t } from '@lingui/core/macro';
import { CustomerDTO, RfqListItemDTO, SupplierAndStockLocationDTO, UserType } from '@luminovo/api-contracts';
import { isAuthorized, usePermissions } from '@luminovo/auth';
import { compareByDate, compareByStringKey } from '@luminovo/commons';
import { GlobalCommandDefinition, SearchableResource, useHttpQuery } from '@luminovo/data-stack';
import { colorSystem, Flexbox, linkOptions, Tag, Text, useCanOpen, useNavigate } from '@luminovo/design-system';
import { transEnum } from '@luminovo/frontend-utils';
import { rfqStatusTranslations } from '@luminovo/sourcing-core';
import React from 'react';

import { rfqStatusColor } from '@/areas/projects/common/RfqStatusBox';
import { useCurrentUserDetailsContext } from '@/shared/data/contexts/CurrentUserDetailsContext';

// The page each search's results open, which decides who gets the search, and where `navigate` sends a pick: only `to`
// matters for the lookup, the params are filled in per result.
const resultPages = {
    rfqs: linkOptions({ to: '/projects/$projectId/dashboard', params: { projectId: '' } }),
    customers: linkOptions({ to: '/customers/$customerId', params: { customerId: '' } }),
    suppliers: linkOptions({
        to: '/suppliers/$supplierAndStockLocationId',
        params: { supplierAndStockLocationId: '' },
    }),
};

/** Client-side search over cached lists; non-suspense queries, so the navbar never waits for them. */
export function useSearchableResources(): SearchableResource<unknown>[] {
    const { permissions } = usePermissions();
    const { userType } = useCurrentUserDetailsContext();
    const canOpen = useCanOpen();
    const navigate = useNavigate();

    const { data: rfqs = [] } = useHttpQuery(
        'GET /rfqs',
        { queryParams: { filter: 'all' } },
        { select: (res) => Array.from(res.data).sort(compareByDate((a) => a.creation_date)) },
    );

    const { data: customers = [] } = useHttpQuery(
        'GET /customers',
        {},
        { select: (data) => data.data.sort(compareByStringKey((customer) => customer.name)) },
    );

    const { data: suppliersAndStockLocations = [] } = useHttpQuery(
        'GET /suppliers-and-stock-locations',
        {},
        {
            select: (res) => Array.from(res.data).sort(compareByStringKey((x) => x.supplier.name)),
        },
    );

    if (userType !== UserType.Internal) {
        return [];
    }

    const sources: SearchableResource<unknown>[] = [];

    // The casts are safe: each resource's entities and `getSearchableText` always travel together.
    if (canOpen(resultPages.rfqs)) {
        sources.push(createRfqSearchableResource(rfqs, navigate) as SearchableResource<unknown>);
    }

    // `view:customer:all` is for the list the search reads; the page a result opens asks for its own access.
    if (isAuthorized(permissions, ['view:customer:all']) && canOpen(resultPages.customers)) {
        sources.push(createCustomerSearchableResource(customers, navigate) as SearchableResource<unknown>);
    }

    if (canOpen(resultPages.suppliers)) {
        sources.push(
            createSupplierSearchableResource(suppliersAndStockLocations, navigate) as SearchableResource<unknown>,
        );
    }

    return sources;
}

function createRfqSearchableResource(
    rfqs: RfqListItemDTO[],
    navigate: ReturnType<typeof useNavigate>,
): SearchableResource<RfqListItemDTO> {
    return {
        id: 'rfqs',
        groupLabel: () => t`Projects`,
        entities: rfqs,
        getSearchableText: (rfq) => {
            return `${rfq.name} ${rfq.ems_internal_number ?? ''} ${rfq.customer_name} ${rfq.customer_number ?? ''}`;
        },
        toCommand: (rfq): GlobalCommandDefinition => ({
            id: `rfq-${rfq.id}`,
            label: () => rfq.name,
            keywords: [rfq.customer_name, rfq.ems_internal_number ?? '', rfq.customer_number ?? ''].filter(Boolean),
            renderContent: () => <RfqCommandContent rfq={rfq} />,
            onSelect: (ctx) => {
                navigate({
                    to: resultPages.rfqs.to,
                    params: { projectId: rfq.id },
                    openInNewTab: ctx.openInNewTab,
                });
                ctx.close();
            },
        }),
        maxResults: 5,
        minQueryLength: 2,
    };
}

function RfqCommandContent({ rfq }: { rfq: RfqListItemDTO }): JSX.Element {
    return (
        <Flexbox
            gap="4px"
            sx={{
                flexDirection: 'column',
            }}
        >
            <Flexbox
                gap="8px"
                sx={{
                    alignItems: 'center',
                }}
            >
                <Text variant="body-semibold" color={colorSystem.neutral[9]} style={{ flex: 1 }}>
                    {rfq.name}
                </Text>
                <Tag
                    attention="low"
                    color={rfqStatusColor(rfq.status)}
                    label={transEnum(rfq.status, rfqStatusTranslations)}
                />
            </Flexbox>
            <Flexbox
                gap="8px"
                sx={{
                    alignItems: 'center',
                }}
            >
                <Text variant="body-small" color={colorSystem.neutral[7]}>
                    {rfq.customer_name}
                    {rfq.customer_number && ` · ${rfq.customer_number}`}
                </Text>
                {rfq.ems_internal_number && (
                    <Text variant="body-small" color={colorSystem.neutral[5]}>
                        {rfq.ems_internal_number}
                    </Text>
                )}
            </Flexbox>
        </Flexbox>
    );
}

function createCustomerSearchableResource(
    customers: CustomerDTO[],
    navigate: ReturnType<typeof useNavigate>,
): SearchableResource<CustomerDTO> {
    return {
        id: 'customers',
        groupLabel: () => t`Customers`,
        entities: customers,
        getSearchableText: (customer) => `${customer.name} ${customer.customer_number ?? ''}`,
        toCommand: (customer): GlobalCommandDefinition => ({
            id: `customer-${customer.id}`,
            label: () => customer.name,
            description: customer.customer_number ? () => customer.customer_number! : undefined,
            onSelect: (ctx) => {
                navigate({
                    to: resultPages.customers.to,
                    params: { customerId: customer.id },
                    openInNewTab: ctx.openInNewTab,
                });
                ctx.close();
            },
        }),
        maxResults: 5,
        minQueryLength: 2,
    };
}

function createSupplierSearchableResource(
    suppliersAndStockLocations: SupplierAndStockLocationDTO[],
    navigate: ReturnType<typeof useNavigate>,
): SearchableResource<SupplierAndStockLocationDTO> {
    return {
        id: 'suppliers',
        groupLabel: () => t`Suppliers`,
        entities: suppliersAndStockLocations,
        getSearchableText: (supplierAndStockLocation) =>
            `${supplierAndStockLocation.supplier.name} ${supplierAndStockLocation.supplier.supplier_number ?? ''}`,
        toCommand: (supplierAndStockLocation): GlobalCommandDefinition => ({
            id: `supplier-${supplierAndStockLocation.id}`,
            label: () => supplierAndStockLocation.supplier.name,
            description: supplierAndStockLocation.supplier.supplier_number
                ? () => supplierAndStockLocation.supplier.supplier_number!
                : undefined,
            onSelect: (ctx) => {
                navigate({
                    to: resultPages.suppliers.to,
                    params: {
                        supplierAndStockLocationId: supplierAndStockLocation.id,
                    },
                    openInNewTab: ctx.openInNewTab,
                });
                ctx.close();
            },
        }),
        maxResults: 5,
        minQueryLength: 2,
    };
}
